import { Separator } from "@components/ui/separator";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbLink,
  BreadcrumbPage,
} from "@components/ui/breadcrumb";
import { isBefore } from "date-fns";
import {
  ChevronLeftIcon,
  DownloadIcon,
  ListFilterIcon,
  SendIcon,
} from "lucide-react";
import { useState } from "react";
import {
  generatePath,
  Link,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { getSearchFilterOptions } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { Button } from "@/components/ui/button";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { useUser } from "@/hooks/useUser";
import { RELATIVE_PATHS } from "@/routes_new";
import { useSearchSectionRoutes } from "@/hooks/useSearchSectionRoutes";
import { EVENT } from "@/tracking_events";
import QuickSubscribe from "@/views/MentionsView/partials/QuickSubscribe";
import AllEarsTitle from "@components/AllEarsTitle";

import { useOrganization } from "@/hooks/useOrganization";
import { Trans, useLingui } from "@lingui/react/macro";
import { Spinner } from "@components/ui/spinner";
import SearchExportDialog from "../partials/SearchExportDialog";
import SearchShareDialog from "../partials/SearchShareDialog";
import SearchFilter from "@components/SearchFilter";
import SearchFilterBar from "@components/SearchFilterBar";
import { useFilterOrder } from "@components/SearchFilterBar/filterOrder";
import { useInsightView } from "@/hooks/useInsightView";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import DialogReach from "@components/SearchFilter/partials/DialogReach";
import DialogFreeText from "@components/SearchFilter/partials/DialogFreeText";
import SearchDates from "@components/SearchDates";
import { useLightSearch } from "@/hooks/useLightSearch";
import { omitPageParams } from "@/hooks/usePageParam";

type MentionLayoutProps = { children: React.ReactNode; url: string };

const MentionLayout: React.FunctionComponent<MentionLayoutProps> = ({
  children,
  url,
}) => {
  const { t } = useLingui();

  // URL state
  const { orgUuid, uuid } = useParams<{ orgUuid: string; uuid: string }>();
  const [filters, setFilters] = useFilterParamsV4();
  const location = useLocation();
  const insight = useInsightView();
  const section = useSearchSectionRoutes();

  const insightsPages = [
    {
      path: `${url}${RELATIVE_PATHS.ALERT_INSIGHTS_REACH}`,
      label: t`Räckvidd`,
    },
    {
      path: `${url}${RELATIVE_PATHS.ALERT_INSIGHTS_SENTIMENT}`,
      label: t`Sentiment`,
    },
    {
      path: `${url}${RELATIVE_PATHS.ALERT_INSIGHTS_RESULTS}`,
      label: t`Träffar`,
    },
  ];

  const insightsMatch = useRouteMatch(insightsPages.map((page) => page.path));

  const insightsLabel = insightsPages.find(
    (page) => page.path === insightsMatch?.path
  )?.label;

  // Server state
  const search = useLightSearch(uuid, orgUuid);

  // Local state
  const [filterOrder, handleFilterChange] = useFilterOrder(
    filters,
    setFilters,
    search?.start_date,
    { insight }
  );
  const [isFreeTextOpen, setIsFreeTextOpen] = useState<boolean>(false);
  const [isReachOpen, setIsReachOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);

  // Server state
  const { data: user } = useUser();
  const { id: user_id = "" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { plan } = orgData?.ok ? orgData.data : {};
  const allowedIntervals =
    plan?.notification_subscription_intervals || undefined;

  const {
    data: searchFilterOptionsData,
    error: searchFilterOptionsError,
    isLoading: isSearchFilterOptionsLoading,
  } = useQuery({
    queryKey: ["searches", uuid, "filter-options"],
    queryFn: () => getSearchFilterOptions(uuid),
  });

  // Helpers
  const archived = search
    ? search.end_date && isBefore(new Date(search.end_date), new Date())
    : false;
  const detailLink = `${generatePath(section.detail, {
    orgUuid,
    uuid,
  })}${omitPageParams(location.search)}`;

  const isInitializing = search?.is_initializing;

  return (
    <>
      <AllEarsTitle title={search?.title} />
      {!search ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : (
        <>
          <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
            <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-(--site-content-max-width)">
              <SidebarTrigger className="-ml-1" />

              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />

              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <Trans>Bevakningar</Trans>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  {insightsLabel ? (
                    <>
                      <BreadcrumbItem>
                        <BreadcrumbLink render={<Link to={detailLink} />}>
                          {search?.title}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbPage>{insightsLabel}</BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  ) : (
                    <BreadcrumbItem>
                      <BreadcrumbPage>{search?.title}</BreadcrumbPage>
                    </BreadcrumbItem>
                  )}
                </BreadcrumbList>
              </Breadcrumb>

              <div className="flex items-center gap-2 ml-auto">
                <Button
                  data-testid="open-share-modal"
                  className="md:flex"
                  onClick={() => {
                    postTrackingData(EVENT.SHARE_ALERT);
                    setIsSharing(true);
                  }}
                  variant="outline"
                  size="icon"
                >
                  <SendIcon />
                  <span className="sr-only">
                    <Trans>Dela</Trans>
                  </span>
                </Button>

                <Button
                  data-testid="open-export-modal"
                  onClick={() => {
                    postTrackingData(EVENT.EXPORT_ALERT);
                    setIsExporting(true);
                  }}
                  variant="outline"
                >
                  <Trans>Exportera</Trans>
                  <DownloadIcon />
                </Button>

                {!archived && user_id && orgUuid && (
                  <QuickSubscribe
                    className="hidden md:flex"
                    user_id={user_id}
                    organization_uuid={orgUuid}
                    search_uuid={uuid}
                    allowedIntervals={allowedIntervals}
                  />
                )}
              </div>
            </div>
          </header>

          <div className="p-6 max-w-(--site-content-max-width) flex flex-col gap-6">
            <div className="w-full flex justify-between items-end">
              <div className="flex flex-col gap-2.5 items-start">
                {insightsMatch ? (
                  <Link
                    to={detailLink}
                    aria-label={t`Tillbaka`}
                    className="flex items-center gap-2 w-full group"
                  >
                    <ChevronLeftIcon className="size-5 text-muted-foreground group-hover:text-foreground transition" />

                    <h1
                      data-testid="mentions-title"
                      className="text-2xl font-bold line-clamp-1 flex-1"
                    >
                      {insightsLabel || <Trans>Tillbaka</Trans>}
                    </h1>
                  </Link>
                ) : (
                  <h1
                    data-testid="mentions-title"
                    className="text-2xl font-bold w-full line-clamp-1"
                  >
                    {search?.title}
                  </h1>
                )}

                {isInitializing ? (
                  <div className="flex items-center gap-2">
                    <Spinner />
                    <p className="text-muted-foreground">
                      <Trans>Söker efter historiska omnämnanden.</Trans>
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-3">
                <SearchDates
                  searchStartDate={search?.start_date}
                  onChange={handleFilterChange}
                  selectedEndDate={filters.end_date}
                  selectedStartDate={filters.start_date}
                  minStartDate={
                    searchFilterOptionsData?.ok
                      ? searchFilterOptionsData.data.min_start_date
                      : undefined
                  }
                  maxEndDate={
                    searchFilterOptionsData?.ok
                      ? searchFilterOptionsData.data.max_end_date
                      : undefined
                  }
                />

                {searchFilterOptionsData ? (
                  searchFilterOptionsData.ok ? (
                    <SearchFilter
                      filterOptions={searchFilterOptionsData.data}
                      setIsFreeTextOpen={setIsFreeTextOpen}
                      setIsReachOpen={setIsReachOpen}
                      onChange={handleFilterChange}
                      value={filters}
                    >
                      <Button data-testid="filter-button">
                        <ListFilterIcon />
                        <Trans>Filter</Trans>
                      </Button>
                    </SearchFilter>
                  ) : (
                    <p className="text-destructive text-sm">
                      <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                    </p>
                  )
                ) : isSearchFilterOptionsLoading ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={<Button className="cursor-progress" />}
                    >
                      <ListFilterIcon />
                      <Trans>Filter</Trans>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Trans>Laddar in filter</Trans>
                    </TooltipContent>
                  </Tooltip>
                ) : searchFilterOptionsError ? (
                  <p className="text-destructive text-sm">
                    <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                  </p>
                ) : null}
              </div>
            </div>

            {searchFilterOptionsData ? (
              searchFilterOptionsData.ok ? (
                <SearchFilterBar
                  filterOptions={searchFilterOptionsData.data}
                  setIsFreeTextOpen={setIsFreeTextOpen}
                  setIsReachOpen={setIsReachOpen}
                  onChange={handleFilterChange}
                  filterOrder={filterOrder}
                  value={filters}
                />
              ) : null
            ) : isSearchFilterOptionsLoading && filterOrder.length > 0 ? (
              <Skeleton className="h-11 w-full rounded-none" />
            ) : searchFilterOptionsError ? null : null}

            {isInitializing ? null : children}
          </div>
        </>
      )}

      <SearchShareDialog
        search={search}
        open={isSharing}
        filters={filters}
        onOpenChange={setIsSharing}
      />

      <SearchExportDialog
        search={search}
        filters={filters}
        open={isExporting}
        onOpenChange={setIsExporting}
      />

      {searchFilterOptionsData?.ok && (
        <>
          <DialogReach
            maxReach={searchFilterOptionsData.data.filter_max_reach_value}
            value={filters.min_reach_threshold}
            onOpenChange={setIsReachOpen}
            onChange={handleFilterChange}
            open={isReachOpen}
          />

          <DialogFreeText
            onOpenChange={setIsFreeTextOpen}
            onChange={handleFilterChange}
            value={filters.freetext}
            open={isFreeTextOpen}
          />
        </>
      )}
    </>
  );
};

export default MentionLayout;
